<script lang="ts">
    import { createEventDispatcher, onMount, tick } from "svelte"
    import hash from "object-hash"
    import {
        mdiBriefcaseDownload,
        mdiChevronUp,
        mdiFileMove,
        mdiTrashCan,
        mdiViewList,
        mdiViewModule,
        mdiContentCopy,
    } from "@mdi/js"
    import Icon from "../../widgets/Icon.svelte"
    import { scrollToTop } from "svelte-scrollto"
    import { newNotification, openOverlay, carts, orderCart, formFoucs } from "../../../store"
    import { updateCart, createAddress, updateAddress, deleteCart, createOrder } from "../../../api"
    import { _ } from "svelte-i18n"
    import { tooltip } from "../../../helper"
    import { debounce } from "lodash"
    import CartTitleField from "./fields/CartTitleField.svelte"
    import Table from "../../widgets/Table.svelte"
    import Checkbox from "../../widgets/Checkbox.svelte"
    import SearchField from "../addresses/SearchField.svelte"
    import AddressForm from "../../widgets/AddressForm.svelte"
    import Modal from "../../widgets/Modal.svelte"
    import CartOperationsSummary from "./CartOperationsSummary.svelte"
    import AdvanceShippingNoticeForm from "./AdvanceShippingNoticeForm.svelte"
    import AssignOperationToCart from "./AssignOperationToCart.svelte"
    import { getTableColumn } from "../../../getTableColumns"
    import ShipmentDetails from "./ShipmentDetails.svelte"
    import { emptyAddressObj } from "../../../helperObjects.js"
    import { normalizeVat } from "../../../../../api/hooks/config-client"

    export let cart: Cart
    export let dealer: Dealer

    const dispatch = createEventDispatcher()
    let forceUpdate = false
    let open: boolean = true // $carts?.length < 2 ? true : false
    let sort: string = "-number"
    let editAddress = false

    let addressFormDataHash: string = null
    let gridview = false
    let innerWidth = window.innerWidth
    const gridBreakPoint = 480
    const emptyAddress = JSON.parse(JSON.stringify(emptyAddressObj))
    let addressFormElement: HTMLFormElement
    // @ts-ignore
    let addressFormData: Address = JSON.parse(JSON.stringify(emptyAddress))
    let updateFormDataByAddress: (data: Address) => Promise<void>
    let resetForm: () => void
    let formSubmitAction: string
    $: isFormReadonly = cart.deliveryAddressIsHomeAddress

    let advanceShippingNoticeChecked: boolean = false
    let confirmAddressUpdate: Address = null
    let showConfirmAddressUpdateModal: boolean = false
    let confirmTakeOverData: Operation | OperationOut = null
    let deleteCartOperation: Operation | OperationOut = null
    let moveCartOperation: Operation | OperationOut = null
    let showMoveCartOperationModal: boolean = true
    let copyCartOperation: Operation | OperationOut = null
    let showCopyCartOperationModal: boolean = true

    const getOperationTableColumns: () => TableColumn[] = () => [...getTableColumn(false, "cart")]

    const addressFormAction = async (e: MouseEvent & { currentTarget: HTMLButtonElement }) => {
        if (!addressFormElement.checkValidity()) {
            addressFormElement.reportValidity()
            newNotification({
                class: "error",
                html: $_("cart.missingRequiredFields"),
            })
            return
        }
        formSubmitAction = null
        await tick()
        // currentTarget statt target: der Handler hängt am Button selbst
        // (Svelte übergibt ein MouseEvent, dessen target EventTarget ist).
        formSubmitAction = e.currentTarget.name
    }

    // AddressForm dispatcht `{ action, form }` – `action` ist die gewünschte
    // Folgeaktion ("update-cart", "send-order", …), `form` die Adressdaten.
    // Das Detail ist also keine Cart, sondern dieses Paar.
    const submitAddressForm = (e: { detail: { action: string; form: Address } }) => {
        if (e?.detail?.action === "update-cart" && !$orderCart && $orderCart !== false) {
            updateCartByAddressFormEvent(e.detail)
        }

        if (e?.detail?.action === "send-order") {
            if (advanceShippingNoticeChecked) {
                if (cart?.advanceShippingNotice?.name === "" || cart?.advanceShippingNotice?.phone === "") {
                    newNotification({
                        class: "error",
                        html: $_("cart.phoneNotificationChosen"),
                    })
                    return
                }
            }

            cart.deliveryAddress = getDeliveryAddressByForm(e?.detail?.form)
            // orderCart trägt nur den Marker für den laufenden Bestellvorgang
            // (siehe OrderCartMarker in store.ts); form/action gehören dort nicht hin.
            $orderCart = { name: cart.name, cartID: cart.id }
        }
    }

    const sendOrder = () => {
        const cartCopy = JSON.parse(JSON.stringify(cart))
        if (!advanceShippingNoticeChecked) {
            cartCopy.advanceShippingNotice = null
        }
        delete cartCopy.id

        createOrder(cartCopy)
            .then(async () => {
                $orderCart = false
                scrollToTop()
                deleteCartFromCartsCollection().then(() => {
                    $orderCart = null
                })
                newNotification({
                    class: "success",
                    html: $_("cart.orderSent"),
                })
            })
            .catch((e) => {
                newNotification({
                    class: "error",
                    html: $_("cart.orderFailed") + e,
                })
            })
    }

    const deleteCartFromCartsCollection = async () => {
        deleteCart(cart)
            .then(() => {
                $carts = $carts.filter((c) => c.id !== cart.id)
                newNotification({
                    class: "success",
                    html: $_("cart.cartDeleted"),
                })
            })
            .catch((e) => {
                newNotification({
                    class: "error",
                    html: $_("cart.cartDeleteFailed"),
                })
            })
    }

    const updateCartByAddressFormEvent = (data: { action: string; form: Address }) => {
        const deliveryAddress: OperationAddress = getDeliveryAddressByForm(data.form)
        const fullDelivery = JSON.parse(JSON.stringify(data.form))
        fullDelivery.vat = normalizeVat(fullDelivery.vat)
        const updatedCart = {
            id: cart.id,
            ident: cart.ident,
            name: cart.name,
            dontShipBefore: cart.dontShipBefore,
            operations: cart.operations,
            commission: cart.commission,
            constructionProject: cart.constructionProject,
            deliveryAddress: deliveryAddress,
            advanceShippingNotice: cart.advanceShippingNotice,
            deliveryAddressIsHomeAddress: cart?.deliveryAddressIsHomeAddress,
            customerAddress: fullDelivery,
        } as Cart

        // Hinweis: Hier stand früher
        //     if (data.parentCompany) updatedCart.deliveryAddress.name1 = data?.parentCompany?.company?.name
        // Das war nie erreichbar: Das Detail des AddressForm-Events ist
        // `{ action, form }` (AddressForm.svelte:137/337, beide ohne Spread) und
        // trägt kein `parentCompany` auf oberster Ebene. `parentCompany` ist an
        // einer Person nur ein ID-String (`form.person.parentCompany`).
        //
        // Das Stammunternehmen (parentCompany) wird dort verarbeitet, wo das
        // vollständige Address-Objekt noch vorliegt: AddressForm.fillParentCompany
        // lädt es anhand der ID und schreibt daraus `deliveryAddress.name1`
        // (AddressForm.svelte:174 aus der Lieferadresse der Stammfirma, sonst
        // :193 aus `company.name`). In Cart.svelte ist das Objekt nicht mehr vorhanden.
        updateCartDebounce(updatedCart)
    }

    // Liefert immer eine OperationAddress: entweder die im Formular gesetzte
    // Lieferadresse oder eine aus Firmen-/Personen-/Standortfeldern gebaute.
    const getDeliveryAddressByForm = (form: Address): OperationAddress => {
        if (form.deliveryAddress) return form.deliveryAddress
        return {
            salutation: form?.person?.salutation || "",
            name1: form?.company?.name || "",
            name2: form?.location?.supplement || "",
            name3: ((form?.person?.lastname || "") + " " + (form?.person?.firstname || "")).substring(0, 30) || "",
            street: form?.location?.street ? `${form?.location?.street} ${form?.location?.houseNumber}` : "",
            postcode: form?.location?.postcode || "",
            city: form?.location?.city || "",
            country: form?.location?.country || "",
            phone: form?.contact?.phone || "",
            email: form?.contact?.email || "",
            fax: form?.contact?.fax || "",
        }
    }

    function setHomeAddress() {
        // Home Address
        if (!addressFormData.location) addressFormData.location = {}
        if (!addressFormData.person) {
            addressFormData.person = {
                firstname: "",
                lastname: "",
                salutation: "",
            }
        }
        if (!addressFormData.contact) addressFormData.contact = {}

        addressFormData.company.name = dealer?.company || ""
        addressFormData.vat = normalizeVat(dealer?.vat)
        addressFormData.company.vatId = dealer?.vatId || ""

        addressFormData.person.firstname = dealer?.company || ""

        addressFormData.location.city = dealer?.city || ""
        addressFormData.location.country = dealer?.countryCode || ""
        addressFormData.location.postcode = dealer?.postcode || ""

        const streetParts = dealer?.street?.split(" ")
        if (streetParts?.length > 1) {
            addressFormData.location.houseNumber = streetParts?.pop()
            addressFormData.location.street = streetParts?.join(" ")
        } else {
            addressFormData.location.street = dealer?.street || ""
            addressFormData.location.houseNumber = ""
        }
        cart.customerAddress = JSON.parse(JSON.stringify(addressFormData))
    }

    const setFormDataByCart = () => {
        if (cart?.customerAddress) {
            addressFormData = JSON.parse(JSON.stringify(cart?.customerAddress))
        } else if (cart.deliveryAddressIsHomeAddress && dealer) {
            setHomeAddress()
        } else if (!cart.deliveryAddressIsHomeAddress && cart.deliveryAddress) {
            if (!addressFormData.location) addressFormData.location = {}
            // @ts-ignore
            if (!addressFormData.person) addressFormData.person = {}
            if (!addressFormData.contact) addressFormData.contact = {}
            addressFormData.location.city = cart?.deliveryAddress?.city || ""
            addressFormData.location.country = cart?.deliveryAddress?.country || ""
            addressFormData.location.postcode = cart?.deliveryAddress?.postcode || ""
            const streetParts = cart?.deliveryAddress?.street?.split(" ")
            if (streetParts?.length > 1) {
                addressFormData.location.houseNumber = streetParts?.pop()
                addressFormData.location.street = streetParts?.join(" ")
            } else {
                addressFormData.location.street = cart?.deliveryAddress?.street || ""
                addressFormData.location.houseNumber = ""
            }
            addressFormData.person.salutation = cart?.deliveryAddress?.salutation || ""
            addressFormData.contact.phone = cart?.deliveryAddress?.phone || ""
            addressFormData.contact.email = cart?.deliveryAddress?.email || ""
            addressFormData.contact.fax = cart?.deliveryAddress?.fax || ""
            addressFormData.company.name = ""
            let personName = cart.deliveryAddress?.name3?.split(" ")
            if (personName && personName.length > 1) {
                addressFormData.person.lastname = personName[0] || ""
                addressFormData.person.firstname = personName[1] || ""
            } else if (personName && personName.length == 1) {
                addressFormData.person.lastname = personName[0] || ""
                addressFormData.person.firstname = ""
            } else {
                addressFormData.person.firstname = ""
                addressFormData.person.lastname = ""
            }
            addressFormData.type = cart?.customerAddress?.type || personName?.length ? "person" : "company"
        }

        if (
            !addressFormData.deliveryAddress?.name1 &&
            !addressFormData.deliveryAddress?.name2 &&
            !addressFormData.deliveryAddress?.name3
        ) {
            // @ts-ignore
            addressFormData.deliveryAddress = {}
            addressFormData.deliveryAddress.city = addressFormData.location?.city || ""
            addressFormData.deliveryAddress.country = addressFormData.location?.country || ""
            addressFormData.deliveryAddress.postcode = addressFormData.location?.postcode || ""

            addressFormData.deliveryAddress.street =
                (addressFormData.location?.street || "") +
                (addressFormData.location?.houseNumber ? " " : "") +
                (addressFormData.location?.houseNumber || "")
            addressFormData.deliveryAddress.name1 = addressFormData.company?.name || ""
            addressFormData.deliveryAddress.name2 = addressFormData.location?.supplement || ""
            addressFormData.deliveryAddress.name3 = (
                (addressFormData.person?.lastname || "") +
                (addressFormData.person?.firstname ? ", " : "") +
                (addressFormData.person?.firstname || "")
            ).substring(0, 30)

            addressFormData.deliveryAddress.salutation = addressFormData.person?.salutation || ""
            addressFormData.deliveryAddress.phone = addressFormData.contact?.phone || ""
            addressFormData.deliveryAddress.fax = addressFormData.contact?.fax || ""
            addressFormData.deliveryAddress.email = addressFormData.contact?.email || ""
        }
    }

    const onAddressSelected = async (e: { detail: any }) => {
        updateFormDataByAddress(e.detail || JSON.parse(JSON.stringify(emptyAddress)))
    }
    const saveOrUpdateAddress = () => {
        if (confirmAddressUpdate.id) {
            updateAddress(confirmAddressUpdate)
                .then(() => {
                    newNotification({
                        class: "success",
                        html: $_("cart.addressUpdated"),
                    })
                })
                .catch((e) => {
                    newNotification({
                        class: "error",
                        html: $_("cart.addressUpdateFailed"),
                    })
                    Object.keys(e?.data?.validationErrors || {}).forEach((e) => {
                        let el = document.getElementById(e.split(".").at(-1) + "-" + cart.id)
                        el?.classList.add("invalid")
                        el.oninput = () => {
                            el?.classList.remove("invalid")
                        }
                    })
                })
                .finally(() => {
                    showConfirmAddressUpdateModal = false
                })
        } else {
            createAddress(confirmAddressUpdate)
                .then((e) => {
                    newNotification({
                        class: "success",
                        html: $_("cart.addressCreated"),
                    })
                    addressFormData.id = e.data.id
                })
                .catch((e) => {
                    newNotification({
                        class: "error",
                        html: $_("cart.addressCreateFailed"),
                    })
                    Object.keys(e?.data?.validationErrors || {}).forEach((e) => {
                        let el = document.getElementById(e.split(".").at(-1) + "-" + cart.id)
                        el?.classList.add("invalid")
                        el.oninput = () => {
                            el?.classList.remove("invalid")
                        }
                    })
                })
                .finally(() => {
                    showConfirmAddressUpdateModal = false
                })
        }
    }

    const getOperationButtons = () => {
        const buttons = [
            {
                label: $_("cart.dataOvertake"),
                svgPath: mdiBriefcaseDownload,
                className: "offer-data-takeover",
                onClick(op: Operation | OperationOut) {
                    confirmTakeOverData = op
                },
                getTooltip(op: Operation) {
                    let tooltipContent = $_("cart.dataTakeover") + "<br/>"
                    const opOut = op.operationOutOfferObj || op

                    if (
                        opOut.endCustomerAddress?.name1 ||
                        opOut.endCustomerAddress?.name2 ||
                        opOut.endCustomerAddress?.name3
                    ) {
                        tooltipContent +=
                            "<br/><strong>" +
                            $_("cart.shippingAddress") +
                            "</strong><br/>" +
                            (opOut.endCustomerAddress?.name1 ? opOut.endCustomerAddress.name1 + "</br>" : "") +
                            (opOut.endCustomerAddress?.name2 || "") +
                            " " +
                            (opOut.endCustomerAddress?.name3 || "") +
                            "<br/>"
                    }
                    if (opOut.commission) {
                        tooltipContent +=
                            "<br/><strong>" + $_("cart.commission") + "</strong><br/>" + opOut.commission + "<br/>"
                    }
                    if (opOut.constructionProject) {
                        tooltipContent +=
                            "<br/><strong>" +
                            $_("cart.constructionProject") +
                            "</strong><br/>" +
                            opOut.constructionProject +
                            "<br/>"
                    }

                    if (
                        !opOut.endCustomerAddress?.name1 &&
                        !opOut.endCustomerAddress?.name2 &&
                        !opOut.endCustomerAddress?.name3 &&
                        !opOut.commission &&
                        !opOut.constructionProject
                    ) {
                        tooltipContent = $_("cart.noDataAvailable")
                    }

                    return {
                        content: tooltipContent,
                    }
                },
            },
            {
                label: $_("cart.moveOfferS"),
                className: "move-offer",
                svgPath: mdiFileMove,
                onClick(op: Operation | OperationOut) {
                    moveCartOperation = op
                    showMoveCartOperationModal = true
                },
                getTooltip(op: any) {
                    return {
                        content: $_("cart.moveOffer"),
                    }
                },
                getDisabledTooltip(op: any) {
                    return {
                        content: $_("cart.moveOfferDisabled"),
                    }
                },
                disabled() {
                    return $carts.length === 1
                },
            },
            {
                label: $_("cart.copyOfferS"),
                className: "copy-offer",
                svgPath: mdiContentCopy,
                onClick(op: Operation | OperationOut) {
                    copyCartOperation = op
                    showCopyCartOperationModal = true
                },
                getTooltip(op: any) {
                    return {
                        content: $_("cart.copyOffer"),
                    }
                },
                getDisabledTooltip(op: any) {
                    return {
                        content: $_("cart.copyOfferDisabled"),
                    }
                },
                disabled() {
                    return $carts.length === 1
                },
            },
            {
                label: $_("general.delete"),
                className: "delete-offer",
                svgPath: mdiTrashCan,
                onClick(op: Operation | OperationOut) {
                    deleteCartOperation = op
                },
                getTooltip(op: any) {
                    return {
                        content: $_("cart.deleteOffer"),
                    }
                },
            },
        ]

        return buttons
    }

    const takeOverData = () => {
        // @ts-ignore
        const op = confirmTakeOverData.operationOutOfferObj || confirmTakeOverData
        if (op.commission) {
            cart.commission = op.commission
        }
        if (op.constructionProject) {
            cart.constructionProject = op.constructionProject
        }
        if (op.endCustomerAddress) {
            cart.deliveryAddressIsHomeAddress = false
            cart.deliveryAddress = JSON.parse(JSON.stringify(op.endCustomerAddress))
            cart.customerAddress = JSON.parse(JSON.stringify(op?.endCustomerFullAddress || {}))
            if (confirmTakeOverData.endCustomerAddress) {
                cart.customerAddress.deliveryAddress = JSON.parse(
                    JSON.stringify(confirmTakeOverData.endCustomerAddress)
                )
            }
            cart.customerAddress.type = "company"
        }

        // Close modal
        confirmTakeOverData = null
    }

    const deleteOperationFromCart = () => {
        const index = cart.operations.findIndex((op) => op.id === deleteCartOperation.id)
        if (index > -1) {
            cart.operations.splice(index, 1)
            cart.operations = cart.operations
        }
        console.log("cart.operations", cart.operations)
        forceUpdate = true
        updateCartDebounce(cart)

        deleteCartOperation = null
    }

    const cartUpdated = async () => {
        await tick()
        resetForm()
        setFormDataByCart()

        addressFormDataHash = hash(addressFormData)
        cooldown = true
    }

    let showSuccess: boolean
    const updateCartDebounce = debounce((cartObj: Cart) => {
        if (addressFormDataHash === hash(addressFormData) && !forceUpdate) return
        forceUpdate = false
        if ($orderCart || $orderCart === false) return
        updateCart(cartObj)
            .then(() => {
                let obj = $carts?.find((cart) => cart.id == cartObj.id)
                let index = $carts?.indexOf(obj)
                let focused = document.activeElement as HTMLElement
                $formFoucs = focused?.id
                $carts[index] = cartObj
                showSuccess = true
                setTimeout(() => {
                    showSuccess = false
                }, 2000)
            })
            .catch((e) => {
                newNotification({
                    class: "error",
                    html: $_("cart.updateFailed"),
                })
            })
    }, 1)

    let cooldown = false
    let cartDeletion: Cart
    function updateCartObj() {
        if (cooldown) {
            updateCartDebounce(cart)
        }
    }

    $: if (cart) {
        cartUpdated()
        updateCartObj()
    }

    onMount(() => {
        if ($formFoucs) {
            setTimeout(() => {
                let element = document.getElementById($formFoucs)
                element?.focus()
            }, 0)
        }
        innerWidth = window.innerWidth
    })

    let firstRender = true
    $: {
        if (cart && firstRender && resetForm) {
            firstRender = false
            if (cart?.deliveryAddressIsHomeAddress || cart?.deliveryAddressIsHomeAddress !== false) {
                setHomeAddress()
            }
        }
    }
</script>

<svelte:window bind:innerWidth />

<section class="cart" id="cart_{cart.id}" class:show="{open}">
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
        class="cart-title"
        on:click="{() => {
            open = !open
        }}"
        on:keydown
    >
        <CartTitleField
            title="{cart.name}"
            on:change="{(e) => dispatch('title-changed', { name: e.detail, cart })}"
            on:delete="{() => dispatch('delete-cart', cart)}"
        />
        <div class="chevron" class:open>
            <Icon path="{mdiChevronUp}" size="42px" />
        </div>
    </div>
    <div class="cart-content">
        <div class="tabletop layout justify-content-space-between">
            <div class="cart-positions-info">{$_("operationsOut.clickForMoreInfo")}</div>

            {#if innerWidth > gridBreakPoint}
                <div class="layout align-items-center">
                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                    <div
                        class="item"
                        class:active="{!gridview}"
                        on:click="{() => {
                            gridview = false
                        }}"
                        on:keydown
                        use:tooltip="{{ content: $_('operationsOut.tableView') }}"
                    >
                        <Icon path="{mdiViewList}" size="28px" />
                    </div>
                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                    <div
                        class="item"
                        class:active="{gridview}"
                        on:click="{() => {
                            gridview = true
                        }}"
                        on:keydown
                        use:tooltip="{{ content: $_('operationsOut.gridView') }}"
                    >
                        <Icon path="{mdiViewModule}" size="32px" />
                    </div>
                </div>
            {/if}
        </div>

        <Table
            className="operationtable"
            gridview="{gridview || innerWidth <= gridBreakPoint}"
            bind:sort
            columns="{getOperationTableColumns()}"
            buttonsColumn="{{
                label: 'Aktionen',
                className: 'mw-120 w-120 max-w-150',
            }}"
            cartId="{cart?.id}"
            {cart}
            inCart="{true}"
            buttons="{getOperationButtons()}"
            rows="{cart.operations}"
            showPositions="{true}"
            on:click="{(r) => {
                openOverlay(r.detail, 'details', {
                    cartObj: cart,
                    cart: true,
                    overlay: true,
                    cartId: cart.id,
                    operation: r.detail.operation,
                })
            }}"
            on:positionChange="{(r) => {
                cart.operations = r.detail.operations
            }}"
            on:operationDeletion="{(r) => {
                cart.operations = r.detail.operations
                if (cart.operations.length === 0 && cart.id) {
                    cartDeletion = cart
                }
            }}"
        />

        <div class="mt-md">
            <AdvanceShippingNoticeForm
                bind:advanceShippingNotice="{cart.advanceShippingNotice}"
                on:toggle="{(e) => (advanceShippingNoticeChecked = e.detail)}"
                on:update="{() => {
                    forceUpdate = true
                }}"
                bind:avisChecked="{advanceShippingNoticeChecked}"
            />
        </div>

        <CartOperationsSummary {cart} />

        <ShipmentDetails
            bind:cartDontShipBefore="{cart.dontShipBefore}"
            on:update="{(e) => {
                forceUpdate = true
            }}"
        />

        <div class="row mt-lg">
            <div class="col-md-6 no-padding-left">
                <label for="commission">{$_("cart.commission")}</label>
                <input
                    id="commission"
                    type="text"
                    maxlength="{30}"
                    placeholder="{$_('cart.commissionPlaceholder')}"
                    value="{cart.commission || ''}"
                    on:blur="{(e) => {
                        cart.commission = e.currentTarget.value
                        forceUpdate = true
                    }}"
                />
            </div>
            <div class="col-md-6 no-padding-right">
                <label for="construction-project">{$_("cart.constructionProject")}</label>
                <input
                    id="construction-project"
                    type="text"
                    placeholder="{$_('cart.constructionProjectPlaceholder')}"
                    maxlength="{30}"
                    value="{cart.constructionProject || ''}"
                    on:blur="{(e) => {
                        cart.constructionProject = e.currentTarget.value
                        forceUpdate = true
                    }}"
                />
            </div>
        </div>

        <div class="row mb-lg">
            <div class="col-md-12 no-padding">
                <label for="commission">{$_("cart.shippingAddress")}</label>
            </div>
            <div class="col-md-6 no-padding-left" data-cy="homeaddress">
                <Checkbox
                    checked="{cart.deliveryAddressIsHomeAddress}"
                    on:checked="{() => {
                        cart.deliveryAddressIsHomeAddress = true
                        addressFormData = JSON.parse(JSON.stringify(emptyAddress))
                        setHomeAddress()
                        forceUpdate = true
                        updateCartDebounce(cart)
                    }}"
                    on:unchecked="{() => {
                        cart.deliveryAddressIsHomeAddress = false
                        resetForm()
                        cart.customerAddress = JSON.parse(JSON.stringify(addressFormData))
                    }}"
                    label="{$_('cart.homeAddress')}"
                    cssClass="primary with-input-holder"
                />
            </div>
            <div class="col-md-6 no-padding-right" data-cy="shipaddress">
                <Checkbox
                    checked="{!cart.deliveryAddressIsHomeAddress}"
                    on:checked="{() => {
                        cart.deliveryAddressIsHomeAddress = false
                        resetForm()
                        addressFormData.type = 'company'
                        cart.customerAddress = JSON.parse(JSON.stringify(addressFormData))

                        forceUpdate = true
                        updateCartDebounce(cart)
                    }}"
                    on:unchecked="{() => {
                        cart.deliveryAddressIsHomeAddress = true
                        resetForm()
                        setHomeAddress()
                    }}"
                    label="{$_('cart.deviatingShippingAddress')}"
                    cssClass="primary with-input-holder"
                />
            </div>
        </div>

        {#if !cart.deliveryAddressIsHomeAddress}
            <div class="row mt-md">
                <div class="col-md-12 no-padding" style="padding: 0px;" data-cy="search-address">
                    <SearchField
                        headline=" {$_('cart.lookInAddressBook')}"
                        on:selected="{onAddressSelected}"
                        on:clear="{() => {
                            resetForm()
                            addressFormData.type = 'company'
                        }}"
                        currentSelect="{addressFormData?.id}"
                    />
                </div>
            </div>
        {/if}
        <div class="mt-md">
            {#if !editAddress}
                <AddressForm
                    bind:formData="{addressFormData}"
                    bind:formElement="{addressFormElement}"
                    bind:submitAction="{formSubmitAction}"
                    bind:updateFormDataByAddress
                    bind:resetForm
                    on:submit="{submitAddressForm}"
                    bind:readonly="{isFormReadonly}"
                    cartId="{cart?.id}"
                    typeSelection="{!cart?.deliveryAddressIsHomeAddress}"
                    onlyDeliveryFields="{true}"
                    hide="{cart?.deliveryAddressIsHomeAddress}"
                    inCart="{true}"
                />{/if}

            {#if cart?.deliveryAddressIsHomeAddress}
                <div class="mt-md">
                    <div class="row">
                        <div class="col-md-12">
                            <label for="delivery-address">{$_("cart.deliveryAddress")}</label>
                        </div>
                        <div class="col-md-6">
                            {#if addressFormData.deliveryAddress}
                                <div class="address-preview">
                                    <div>
                                        {#if addressFormData.type == "person"}
                                            {addressFormData.deliveryAddress.salutation}
                                            {addressFormData.deliveryAddress.name3}
                                        {:else}
                                            {addressFormData.deliveryAddress.name1}
                                        {/if}
                                    </div>
                                    <div>
                                        {addressFormData.deliveryAddress.street}
                                    </div>
                                    <div>
                                        {addressFormData.deliveryAddress.postcode}
                                        {addressFormData.deliveryAddress.city}
                                        {addressFormData.deliveryAddress.country || ""}
                                    </div>
                                </div>
                            {:else}
                                <div class="address-preview">
                                    {#if addressFormData.type == "person"}
                                        <div>
                                            {addressFormData?.person?.salutation}
                                            {addressFormData?.person?.firstname}
                                            {addressFormData?.person?.lastname}
                                        </div>
                                    {:else}
                                        <div>
                                            {addressFormData?.company?.name}
                                        </div>
                                    {/if}
                                    <div>
                                        {addressFormData?.location?.street}
                                        {addressFormData?.location?.houseNumber}
                                    </div>
                                    <div>
                                        {addressFormData?.location?.postcode}
                                        {addressFormData?.location?.city}
                                        {addressFormData?.location?.country || ""}
                                    </div>
                                </div>
                            {/if}
                        </div>
                    </div>
                </div>
            {/if}
            <div style="display: flex; justify-content: space-between;width: 100%;">
                <div></div>
                <div style="display: flex; gap: 10px;">
                    {#if showSuccess}
                        <div style="color: green; font-size: 0.7rem; display: flex; align-items: flex-end;">
                            {$_("cart.saveData")}
                        </div>
                    {/if}
                    <button
                        class="btn"
                        on:click|preventDefault="{addressFormAction}"
                        name="send-order"
                        data-cy="send-order"
                        disabled="{!cart.operations.length}"
                    >
                        {$_("cart.sendOrder")}
                    </button>
                </div>
            </div>
        </div>
    </div>
</section>
{#if confirmTakeOverData}
    <Modal show="{true}" on:close="{() => (confirmTakeOverData = null)}" showCloseIcon="{false}">
        <div slot="header">{$_("cart.modals.confirmDataTakeover.header")}</div>
        <div>
            {@html $_("cart.modals.confirmDataTakeover.body", { values: { number: confirmTakeOverData.number } })}
        </div>
        <div slot="footer">
            <button class="btn btn-secondary" on:click="{() => (confirmTakeOverData = null)}"
                >{$_("cart.modals.confirmDataTakeover.cancel")}</button
            >
            <button class="btn" on:click="{takeOverData}">{$_("cart.modals.confirmDataTakeover.confirm")}</button>
        </div>
    </Modal>
{/if}

{#if deleteCartOperation}
    <Modal show="{true}" on:close="{() => (deleteCartOperation = null)}" showCloseIcon="{false}">
        <div slot="header">{$_("cart.modals.deleteCartOperation.header")}</div>
        <div>
            {@html $_("cart.modals.deleteCartOperation.body", { values: { number: deleteCartOperation.number } })}
        </div>
        <div slot="footer">
            <button
                class="btn btn-secondary"
                data-cy="cancel-delete-cart"
                on:click="{() => (deleteCartOperation = null)}">{$_("cart.modals.deleteCartOperation.cancel")}</button
            >
            <button class="btn" data-cy="delete-cart" on:click="{deleteOperationFromCart}"
                >{$_("cart.modals.deleteCartOperation.confirm")}</button
            >
        </div>
    </Modal>
{/if}

{#if showConfirmAddressUpdateModal}
    <Modal show="{true}" on:close="{() => (showConfirmAddressUpdateModal = null)}" showCloseIcon="{false}">
        <div slot="header">
            {!confirmAddressUpdate?.id
                ? $_("cart.modals.addressUpdate.headerCreate")
                : $_("cart.modals.addressUpdate.headerUpdate")}
        </div>
        <div>
            {!confirmAddressUpdate?.id
                ? $_("cart.modals.addressUpdate.bodyCreate")
                : $_("cart.modals.addressUpdate.bodyUpdate")}
        </div>
        <div slot="footer">
            <button
                class="btn btn-secondary"
                data-cy="cancel-address"
                on:click="{() => (showConfirmAddressUpdateModal = null)}"
                >{$_("cart.modals.addressUpdate.cancel")}</button
            >
            <button class="btn" data-cy="apply-address" on:click="{saveOrUpdateAddress}">
                {!confirmAddressUpdate?.id
                    ? $_("cart.modals.addressUpdate.confirmCreate")
                    : $_("cart.modals.addressUpdate.confirmUpdate")}
            </button>
        </div>
    </Modal>
{/if}

{#if moveCartOperation}
    <AssignOperationToCart
        operation="{moveCartOperation}"
        moveFromCartToCart="{true}"
        cartId="{cart?.id}"
        title="{$_('cart.modals.moveCartOperation')}"
        bind:show="{showMoveCartOperationModal}"
        on:close="{() => {
            moveCartOperation = null
        }}"
    />
{/if}

{#if copyCartOperation}
    <AssignOperationToCart
        title="{$_('cart.modals.copyCartOperation')}"
        submitButtonLabel="{$_('cart.modals.copy')}"
        operation="{copyCartOperation}"
        copyCartOperation="{true}"
        cartId="{cart?.id}"
        bind:show="{showCopyCartOperationModal}"
        on:close="{() => {
            moveCartOperation = null
        }}"
    />
{/if}

{#if typeof $orderCart === "object" && $orderCart?.name == cart?.name && $orderCart?.cartID == cart?.id}
    <Modal
        show="{true}"
        on:close="{() => {
            $orderCart = null
        }}"
        showCloseIcon="{false}"
    >
        <div slot="header" data-cy="order-cart">{$_("cart.modals.orderCart.header")}</div>
        <div>
            {@html $_("cart.modals.orderCart.body", { values: { name: cart?.name } })}
        </div>
        <div slot="footer">
            <button
                class="btn btn-secondary"
                data-cy="cancel-order"
                on:click="{() => {
                    $orderCart = null
                }}"
            >
                {$_("cart.modals.orderCart.cancel")}
            </button>
            <button class="btn" data-cy="order-it" on:click="{sendOrder}">
                {$_("cart.modals.orderCart.confirm")}
            </button>
        </div>
    </Modal>
{/if}

{#if cartDeletion}
    <Modal show="{true}" on:close="{() => (cartDeletion = null)}" showCloseIcon="{false}">
        <div slot="header">{$_("cart.modals.cartDeletion.header")}</div>
        <div>
            {@html $_("cart.modals.cartDeletion.body", { values: { name: cartDeletion.name } })}
        </div>
        <div slot="footer">
            <button class="btn btn-secondary" on:click="{() => (cartDeletion = null)}">
                {$_("cart.modals.cartDeletion.cancel")}
            </button>
            <button
                class="btn"
                data-cy="delete-cart-since-empty"
                on:click="{async () =>
                    deleteCart(cartDeletion)
                        .then((r) => {
                            $carts = $carts.filter((c) => c.id !== cartDeletion.id)
                            cartDeletion = null
                            newNotification({ class: 'success', html: $_('cart.modals.cartDeletion.success') })
                        })
                        .catch((r) => {
                            cartDeletion = null
                            newNotification({ class: 'error', html: $_('cart.modals.cartDeletion.error') })
                        })}"
            >
                {$_("cart.modals.cartDeletion.confirm")}
            </button>
        </div>
    </Modal>
{/if}
